import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import {
  confirmPasswordFunction,
  confirmUser,
  forgotPasswordRequest,
  sendPhoneNumberCode,
  signUp,
  updateUserAttributes,
  userPool,
  validatePhoneNumberCode,
} from "./auth";
import { confirmUserCodeResponse } from "../../interfaces/auth";

interface UserAttributes {
  given_name?: string;
  phone_number_verified?: string;
  email?: string;
  [key: string]: string | undefined;
}

interface AuthContextType {
  user: CognitoUser | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  userAttributes: UserAttributes | null;
  singUp: (
    email: string,
    password: string,
    attributes: { [key: string]: string }
  ) => Promise<boolean>;
  confirmUserCode: (
    username: string,
    verificationCode: string
  ) => Promise<confirmUserCodeResponse>;
  forgotPassword: (email: string) => Promise<boolean>;
  confirmPassword: (
    username: string,
    verificationCode: string,
    newPassword: string
  ) => Promise<boolean>;
  updateUser: (attributes: any) => Promise<boolean>;
  confirmUserNumber: () => Promise<boolean>;
  validatePhoneNumber: (code: string) => Promise<boolean>;
  handleLoadUserAttributes: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [userAttributes, setUserAttributes] = useState<UserAttributes | null>(
    null
  );

  const handleLoadUserAttributes = () => {
    const storedUser = localStorage.getItem("cognitoUser");
    if (storedUser) {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err: any, session: any) => {
          if (session && session.isValid()) {
            setUser(currentUser);

            currentUser.getUserAttributes((err, attributes) => {
              if (err) {
                console.error(
                  "Error al obtener los atributos del usuario",
                  err
                );
                setLoading(false);
              } else if (attributes) {
                const userAttrs: UserAttributes = {};
                attributes.forEach((attribute: CognitoUserAttribute) => {
                  userAttrs[attribute.Name] = attribute.Value;
                });
                setUserAttributes(userAttrs);
                if (userAttrs) {
                  setLoading(false);
                } else {
                  setTimeout(() => {
                    setLoading(false);
                  }, 1000); // review this
                }
              }
            });
          }
        });
      }
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleLoadUserAttributes();
  }, []);

  const signIn = async (username: string, password: string) => {
    return new Promise<void>((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          localStorage.setItem("cognitoUser", JSON.stringify({ username }));
          setUser(cognitoUser);
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              console.error("Error al obtener los atributos del usuario", err);
            } else if (attributes) {
              const userAttrs: UserAttributes = {};
              attributes.forEach((attribute: CognitoUserAttribute) => {
                userAttrs[attribute.Name] = attribute.Value;
              });
              setUserAttributes(userAttrs);
            }
          });
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const signOut = () => {
    user?.signOut();
    localStorage.removeItem("cognitoUser");
    setUserAttributes(null);
    setUser(null);
  };

  const confirmUserCode = async (
    username: string,
    verificationCode: string
  ) => {
    try {
      const result = await confirmUser(username, verificationCode);
      const response = {
        status: true,
        result: result,
      };
      return response;
    } catch (error) {
      const response = {
        status: false,
        result: error,
      };
      return response;
    }
  };

  const confirmUserNumber = async () => {
    try {
      const result = await sendPhoneNumberCode();
      const response = {
        status: true,
        result: result,
      };

      return true;
    } catch (error) {
      const response = {
        status: false,
        result: error,
      };
      return false;
    }
  };

  const singUp = async (
    email: string,
    password: string,
    attributes: { [key: string]: string }
  ) => {
    const attributesMap = {
      email: attributes.email,
      phone_number: `+506${attributes.phone_number}`,
      given_name: attributes.given_name,
      family_name: attributes.family_name,
    };

    try {
      // const result = await signUp(email, password, attributesMap);
      await signUp(email, password, attributesMap);
      return true;
    } catch (error) {
      return false;
      // Manejar errores específicos de Cognito
      // if (error instanceof Error) {
      //   setErrors({ general: error.message });
      // }
    }
  };

  const confirmPassword = async (
    username: string,
    verificationCode: string,
    newPassword: string
  ) => {
    try {
      const response = await confirmPasswordFunction(
        username,
        verificationCode,
        newPassword
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await forgotPasswordRequest(email);
      return true;
    } catch (error) {
      return false;
    }
  };

  const validatePhoneNumber = async (code: string) => {
    try {
      const response = await validatePhoneNumberCode(code);
      return true;
    } catch (error) {
      return false;
    }
  };

  const updateUser = async (attributes: any) => {
    try {
      const response = await updateUserAttributes(attributes);
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userAttributes,
        signIn,
        signOut,
        isAuthenticated: !!user,
        loading,
        singUp,
        forgotPassword,
        confirmUserCode,
        confirmPassword,
        updateUser,
        confirmUserNumber,
        validatePhoneNumber,
        handleLoadUserAttributes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
